/**
 * Conversational intake definitions and a generic completeness engine (ADR-46).
 *
 * `lot-kinds` already validates a lot's attributes and its release paperwork
 * (registry.ts). Intake is the layer above: when a consignor is TELLING a sale
 * secretary about an item over the phone or by text, which facts make the
 * listing complete, in what order should they be asked, and what does the item
 * sound like in normal speech.
 *
 * The Zod attribute schema says what is VALID; it marks almost everything
 * optional because a cataloger can save a half-filled lot. Intake needs a
 * different axis - what is EXPECTED for a good listing - so each kind gets an
 * `IntakeFieldDef` role (core / recommended / optional) on top of its schema.
 *
 * Horse is deliberately EXTERNAL: the equine ontology + HEKG in
 * `packages/annie` own its ~90-field intake and completeness. This module owns
 * the flat kinds (trailer, cattle, tack, exotic, general). One engine,
 * `itemIntakeStatus`, serves the flat kinds; Annie delegates horse to its own
 * code and everything else here.
 *
 * Nothing in this file reads a database or calls a model. It takes a
 * definition plus a flat facts map (fieldKey -> value) and returns what is
 * still missing - the same posture as settings.ts and policy.ts.
 */

import { missingReleaseDocs, normalizeKind, requiredReleaseDocs } from './index.js';

/** How much an intake field matters for a complete listing. Distinct from Zod
 *  `.optional()`, which is about write validity, not listing readiness. */
export type FieldRole = 'core' | 'recommended' | 'optional';

export interface IntakeFieldDef {
  /** The fact field key. For flat kinds it is ALSO the attribute path, so a
   *  recorded fact maps straight onto `lots.attributes` at materialization.
   *  `reserve` is the one cross-kind special: it maps to `lots.reserve_cents`,
   *  never to attributes. */
  readonly key: string;
  readonly label: string;
  readonly role: FieldRole;
  /** How a spoken value coerces before it is stored. Enums are `string`; the
   *  lot-kind Zod schema validates the actual enum membership at
   *  materialization. Absent = string. */
  readonly valueType?: 'string' | 'number' | 'boolean';
  /** A short natural-language cue for the model ("clean, bonded, or no title"). */
  readonly hint?: string;
  /** Promotes the field to core when the condition holds (a bred cow needs a
   *  due date; a titled trailer needs its VIN). Reads the flat facts map. */
  readonly requiredWhen?: (facts: Readonly<Record<string, unknown>>) => boolean;
}

export interface MediaRequirement {
  readonly minPhotos: number;
  readonly videoRecommended: boolean;
}

export interface KindIntakeDefinition {
  readonly kind: string;
  readonly label: string;
  /** How Annie refers to one of these in conversation ("trailer", "head of
   *  cattle"). Singular. */
  readonly displayName: string;
  /** Normal-speech words that point at this kind. A supporting signal for the
   *  classifier and the allowlist, never the only classification mechanism -
   *  the model still reads the whole sentence. Lowercase, matched whole-word. */
  readonly aliases: readonly string[];
  /** True when this kind's intake completeness and fact vocabulary are owned
   *  elsewhere (horse: the equine ontology). The generic engine skips it. */
  readonly external?: boolean;
  readonly fields: readonly IntakeFieldDef[];
  readonly media: MediaRequirement;
}

/** The `reserve` fact is understood on every kind and never lives in
 *  attributes; it becomes `lots.reserve_cents` at materialization. */
export const RESERVE_KEY = 'reserve';

const reserveField: IntakeFieldDef = {
  key: RESERVE_KEY,
  label: 'Reserve price',
  role: 'recommended',
  valueType: 'number',
  hint: 'a reserve, or no reserve',
};

// ---------------------------------------------------------------------------
// Per-kind intake definitions
// ---------------------------------------------------------------------------

/** Equine (horses, ponies, mules, donkeys, hinnies are ONE kind with an
 *  `equineType` sub-type, not their own catalog kinds - ADR-66). Intake is owned
 *  by the equine ontology in packages/annie; only the classifier metadata lives here. */
const equineIntake: KindIntakeDefinition = {
  kind: 'equine',
  label: 'Horse / Pony',
  displayName: 'horse',
  external: true,
  aliases: [
    'horse', 'mare', 'gelding', 'stallion', 'filly', 'colt', 'foal', 'weanling',
    'yearling', 'broodmare', 'stud', 'pony', 'mini', 'miniature horse',
    'mule', 'molly', 'molly mule', 'john mule', 'donkey', 'jenny', 'jack',
    'burro', 'equine', 'gaited horse',
  ],
  fields: [],
  media: { minPhotos: 3, videoRecommended: true },
};

const trailerIntake: KindIntakeDefinition = {
  kind: 'trailer',
  label: 'Trailer',
  displayName: 'trailer',
  aliases: [
    'trailer', 'horse trailer', 'stock trailer', 'livestock trailer', 'gooseneck',
    'bumper pull', 'slant load', 'stock combo', 'lq trailer', 'living quarters trailer',
  ],
  fields: [
    { key: 'titleStatus', label: 'Title status', role: 'core', hint: 'clean title, bonded, or no title' },
    { key: 'year', label: 'Year', role: 'recommended', valueType: 'number', hint: 'model year' },
    { key: 'make', label: 'Make', role: 'recommended' },
    {
      key: 'vin', label: 'VIN', role: 'recommended',
      requiredWhen: (f) => f['titleStatus'] === 'clean' || f['titleStatus'] === 'bonded',
    },
    { key: 'lengthFt', label: 'Length in feet', role: 'recommended', valueType: 'number' },
    { key: 'model', label: 'Model', role: 'optional' },
    { key: 'axles', label: 'Number of axles', role: 'optional', valueType: 'number' },
    { key: 'livingQuarters', label: 'Living quarters', role: 'optional', valueType: 'boolean', hint: 'does it have living quarters' },
    reserveField,
  ],
  media: { minPhotos: 3, videoRecommended: true },
};

const cattleIntake: KindIntakeDefinition = {
  kind: 'cattle',
  label: 'Cattle',
  displayName: 'head of cattle',
  aliases: [
    'cattle', 'cow', 'bull', 'steer', 'heifer', 'calf', 'pair', 'cow-calf',
    'bovine', 'beef', 'dairy cow', 'bred cow', 'roping steer',
  ],
  fields: [
    { key: 'class', label: 'Class', role: 'core', hint: 'cow, bull, steer, heifer, or pair' },
    { key: 'breed', label: 'Breed', role: 'recommended' },
    { key: 'ageMonths', label: 'Age in months', role: 'recommended', valueType: 'number' },
    { key: 'weightLbs', label: 'Weight in pounds', role: 'recommended', valueType: 'number' },
    { key: 'bred', label: 'Bred', role: 'recommended', valueType: 'boolean', hint: 'is she bred' },
    {
      key: 'dueDate', label: 'Due date', role: 'optional',
      requiredWhen: (f) => f['bred'] === true,
    },
    { key: 'polled', label: 'Polled', role: 'optional', valueType: 'boolean' },
    reserveField,
  ],
  media: { minPhotos: 2, videoRecommended: true },
};

const tackIntake: KindIntakeDefinition = {
  kind: 'tack',
  label: 'Tack & equipment',
  displayName: 'tack item',
  aliases: [
    'tack', 'saddle', 'western saddle', 'english saddle', 'roping saddle', 'bridle',
    'headstall', 'harness', 'blanket', 'saddle pad', 'bit', 'halter', 'breast collar',
  ],
  fields: [
    { key: 'category', label: 'Category', role: 'core', hint: 'saddle, bridle, harness, blanket, or bit' },
    { key: 'maker', label: 'Maker or brand', role: 'recommended' },
    { key: 'condition', label: 'Condition', role: 'recommended', hint: 'new, excellent, good, or fair' },
    { key: 'seatSizeIn', label: 'Seat size in inches', role: 'optional', valueType: 'number' },
    { key: 'model', label: 'Model', role: 'optional' },
    { key: 'material', label: 'Material', role: 'optional' },
    reserveField,
  ],
  media: { minPhotos: 2, videoRecommended: false },
};

const exoticIntake: KindIntakeDefinition = {
  kind: 'exotic',
  label: 'Exotic animal',
  displayName: 'exotic animal',
  // Small ruminants (sheep, goats) and swine map here provisionally until they
  // earn dedicated kinds; the species field carries the specifics.
  aliases: [
    'exotic', 'llama', 'alpaca', 'camel', 'zebra', 'bison', 'buffalo', 'yak', 'watusi',
    'emu', 'ostrich', 'goat', 'sheep', 'lamb', 'pig', 'hog', 'swine', 'pot belly',
  ],
  fields: [
    { key: 'species', label: 'Species', role: 'core', hint: 'what kind of animal' },
    { key: 'commonName', label: 'Common name', role: 'recommended' },
    { key: 'sex', label: 'Sex', role: 'recommended', hint: 'male, female, or unknown' },
    { key: 'ageMonths', label: 'Age in months', role: 'optional', valueType: 'number' },
    { key: 'captiveBred', label: 'Captive bred', role: 'optional', valueType: 'boolean' },
    { key: 'tame', label: 'Tame', role: 'optional', valueType: 'boolean' },
    reserveField,
  ],
  media: { minPhotos: 3, videoRecommended: true },
};

/** The catch-all. Vehicles, ATVs, boats and farm equipment ride here until
 *  they earn dedicated kinds (ADR-46); title/VIN validation comes with those
 *  kinds, not from this junk-drawer. */
const generalIntake: KindIntakeDefinition = {
  kind: 'general',
  label: 'General',
  displayName: 'item',
  aliases: [
    'equipment', 'farm equipment', 'implement', 'machinery', 'tractor', 'truck',
    'pickup', 'vehicle', 'car', 'suv', 'atv', 'utv', 'four wheeler', 'side by side',
    'boat', 'miscellaneous', 'misc', 'other',
  ],
  fields: [reserveField],
  media: { minPhotos: 2, videoRecommended: false },
};

/** Informational catalog rows; never consigned by a customer over the phone.
 *  Present for completeness of the registry, not offered for intake. */
const noticeIntake: KindIntakeDefinition = {
  kind: 'notice',
  label: 'Notice (no bidding)',
  displayName: 'notice',
  aliases: [],
  fields: [],
  media: { minPhotos: 0, videoRecommended: false },
};

export const KIND_INTAKE: ReadonlyMap<string, KindIntakeDefinition> = new Map(
  [equineIntake, trailerIntake, cattleIntake, tackIntake, exoticIntake, generalIntake, noticeIntake].map(
    (d) => [d.kind, d],
  ),
);

export function getKindIntake(kind: string): KindIntakeDefinition {
  const def = KIND_INTAKE.get(normalizeKind(kind));
  if (!def) throw new Error(`no intake definition for lot kind: ${kind}`);
  return def;
}

// ---------------------------------------------------------------------------
// Alias classifier - a supporting signal, never the only classification
// ---------------------------------------------------------------------------

export interface KindMatch {
  readonly kind: string;
  /** The alias that matched, for the audit trail and disambiguation. */
  readonly matched: string;
}

const wordBoundaryHit = (haystack: string, needle: string): boolean => {
  let from = 0;
  for (;;) {
    const i = haystack.indexOf(needle, from);
    if (i < 0) return false;
    const before = i === 0 ? ' ' : haystack[i - 1]!;
    const after = i + needle.length >= haystack.length ? ' ' : haystack[i + needle.length]!;
    if (!/[a-z0-9]/.test(before) && !/[a-z0-9]/.test(after)) return true;
    from = i + needle.length;
  }
};

/**
 * Candidate kinds whose aliases appear as whole words in `text`, ordered by
 * the longest matched alias first (a "stock trailer" beats a bare "stock").
 * At most one match per kind. The caller (Annie's model, then the allowlist)
 * decides; this only narrows.
 */
export function matchItemTypeAliases(text: string): readonly KindMatch[] {
  const t = ` ${text.toLowerCase()} `;
  const hits: { kind: string; matched: string; len: number }[] = [];
  for (const def of KIND_INTAKE.values()) {
    let best: string | null = null;
    for (const alias of def.aliases) {
      if (wordBoundaryHit(t, alias) && (best === null || alias.length > best.length)) best = alias;
    }
    if (best) hits.push({ kind: def.kind, matched: best, len: best.length });
  }
  hits.sort((a, b) => b.len - a.len);
  return hits.map((h) => ({ kind: h.kind, matched: h.matched }));
}

// ---------------------------------------------------------------------------
// Generic completeness engine (flat kinds; horse is external)
// ---------------------------------------------------------------------------

export interface IntakeFieldMiss {
  readonly key: string;
  readonly label: string;
  readonly hint?: string;
}

export interface IntakeDocStatus {
  readonly docType: string;
  readonly label: string;
  readonly status: 'received' | 'missing';
}

export interface IntakeMediaState {
  readonly photos: number;
  readonly videos: number;
  readonly docTypesOnFile: readonly string[];
}

export interface ItemIntakeStatus {
  readonly kind: string;
  /** 0-100, weighted across core facts, recommended facts, release docs,
   *  photos and video. */
  readonly completionPercent: number;
  readonly requiredMissing: readonly IntakeFieldMiss[];
  readonly recommendedMissing: readonly IntakeFieldMiss[];
  readonly documents: readonly IntakeDocStatus[];
  readonly photos: { readonly have: number; readonly minimum: number };
  readonly video: { readonly have: number; readonly recommended: boolean };
  /** Ordered field keys (plus `photos` / `video`) the model may ask next -
   *  core gaps first, then recommended, then media. The model picks one that
   *  sounds natural; HipNumber decides what is actually still missing. */
  readonly nextPossibleQuestions: readonly string[];
}

/** A value counts as present when it is not undefined, null, or an empty
 *  string. `false` and `0` are present (a trailer with 0 living quarters, an
 *  unbred cow that was explicitly answered). */
const present = (v: unknown): boolean => v !== undefined && v !== null && v !== '';

/** True when a field is required for THIS item, base role plus any condition. */
const isCore = (f: IntakeFieldDef, facts: Readonly<Record<string, unknown>>): boolean =>
  f.role === 'core' || (f.requiredWhen?.(facts) ?? false);

/**
 * Build a `lots.attributes`-shaped object from a flat facts map, so the
 * release-doc `requiredWhen` predicates (which read attributes) can be
 * evaluated. Drops `reserve` (it is a column, not an attribute) and any fact
 * key the kind does not declare.
 */
export function factsToAttributes(
  def: KindIntakeDefinition,
  facts: Readonly<Record<string, unknown>>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of def.fields) {
    if (f.key === RESERVE_KEY) continue;
    if (present(facts[f.key])) out[f.key] = facts[f.key];
  }
  return out;
}

const miss = (f: IntakeFieldDef): IntakeFieldMiss =>
  f.hint === undefined ? { key: f.key, label: f.label } : { key: f.key, label: f.label, hint: f.hint };

/**
 * What a flat-kind intake still needs, and how complete it is. `getKindIntake`
 * for `external` kinds (horse) throws in the caller's face on purpose: horse
 * completeness lives in the equine ontology, not here.
 */
export function itemIntakeStatus(
  def: KindIntakeDefinition,
  facts: Readonly<Record<string, unknown>>,
  media: IntakeMediaState,
): ItemIntakeStatus {
  if (def.external) {
    throw new Error(`itemIntakeStatus does not serve external kind '${def.kind}'; use its owning domain`);
  }

  const core = def.fields.filter((f) => isCore(f, facts));
  const recommended = def.fields.filter((f) => f.role === 'recommended' && !isCore(f, facts));
  const requiredMissing = core.filter((f) => !present(facts[f.key])).map(miss);
  const recommendedMissing = recommended.filter((f) => !present(facts[f.key])).map(miss);

  const attributes = factsToAttributes(def, facts);
  const required = requiredReleaseDocs(def.kind, attributes);
  const onFile = new Set(media.docTypesOnFile);
  const missingDocs = new Set(missingReleaseDocs(def.kind, attributes, media.docTypesOnFile).map((d) => d.docType));
  const documents: IntakeDocStatus[] = required.map((d) => ({
    docType: d.docType,
    label: d.label,
    status: onFile.has(d.docType) && !missingDocs.has(d.docType) ? 'received' : 'missing',
  }));

  const ratio = (have: number, need: number): number => (need <= 0 ? 1 : Math.max(0, Math.min(1, have / need)));
  const coreScore = ratio(core.length - requiredMissing.length, core.length);
  const recScore = ratio(recommended.length - recommendedMissing.length, recommended.length);
  const docScore = ratio(documents.filter((d) => d.status === 'received').length, documents.length);
  const photoScore = ratio(media.photos, def.media.minPhotos);
  const videoScore = def.media.videoRecommended ? ratio(media.videos, 1) : 1;
  const completionPercent = Math.round(
    100 * (0.45 * coreScore + 0.15 * recScore + 0.2 * docScore + 0.15 * photoScore + 0.05 * videoScore),
  );

  const nextPossibleQuestions: string[] = [
    ...requiredMissing.map((m) => m.key),
    ...recommendedMissing.map((m) => m.key),
    ...(media.photos < def.media.minPhotos ? ['photos'] : []),
    ...(def.media.videoRecommended && media.videos < 1 ? ['video'] : []),
  ];

  return {
    kind: def.kind,
    completionPercent,
    requiredMissing,
    recommendedMissing,
    documents,
    photos: { have: media.photos, minimum: def.media.minPhotos },
    video: { have: media.videos, recommended: def.media.videoRecommended },
    nextPossibleQuestions,
  };
}
